
import { useEffect, useRef } from "react";
import { useSearchParams } from "react-router-dom";
import { Canvas } from "fabric";

import { designerEngine } from "@/components/designer/engine/DesignerEngine";
import { useDesignerStore } from "@/components/designer/store";
import { meApi } from "@/lib/api";

const BASE_W = 800;
const BASE_H = 700;

export default function StudioCanvas() {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const workspaceRef = useRef<HTMLDivElement>(null);
  const initialized = useRef(false);
  const designLoaded = useRef(false);

  const [params] = useSearchParams();
  const designId = params.get("design");

  const product = useDesignerStore((state) => state.product);
  const side = useDesignerStore((state) => state.side);
  const logo = useDesignerStore((state) => state.logo);

  // Scale the (fixed-resolution) canvas to fit its container using CSS-only
  // dimensions — keeps the backing store crisp and pointer mapping correct.
  function fit() {
    const canvas = designerEngine.getCanvas();
    const ws = workspaceRef.current;
    if (!canvas || !ws) return;
    const cs = getComputedStyle(ws);
    const avail = ws.clientWidth - parseFloat(cs.paddingLeft) - parseFloat(cs.paddingRight);
    const scale = Math.min(1, Math.max(0.1, avail / BASE_W));
    canvas.setDimensions({ width: BASE_W * scale, height: BASE_H * scale }, { cssOnly: true });
  }

  useEffect(() => {
    if (!canvasRef.current) return;
    if (initialized.current) return;

    const fabricCanvas = new Canvas(canvasRef.current, {
      width: BASE_W,
      height: BASE_H,
      backgroundColor: "#ffffff",
      preserveObjectStacking: true,
    });

    designerEngine.initialize(fabricCanvas);

    initialized.current = true;
    fit();
    // Re-fit after layout settles (grid columns apply post-paint).
    const t = setTimeout(fit, 120);

    const ro = new ResizeObserver(() => fit());
    if (workspaceRef.current) ro.observe(workspaceRef.current);
    window.addEventListener("resize", fit);
    window.addEventListener("orientationchange", fit);

    return () => {
      clearTimeout(t);
      ro.disconnect();
      window.removeEventListener("resize", fit);
      window.removeEventListener("orientationchange", fit);
      fabricCanvas.dispose();
      initialized.current = false;
    };
  }, []);

  // Normal product rendering. Suppressed while opening a saved design by URL so
  // the loaded artwork isn't wiped by a product redraw.
  useEffect(() => {
    if (!initialized.current) return;
    if (designId) return;
    designerEngine.loadProduct(product).then(fit);
  }, [product, designId]);

  // Reopen a saved design (?design=<id>) once.
  useEffect(() => {
    if (!initialized.current || !designId || designLoaded.current) return;
    designLoaded.current = true;
    meApi
      .design(designId)
      .then((r) => {
        const kind = r.design.kind === "gangsheet" ? "gangsheet" : "tshirt";
        useDesignerStore.getState().setProduct(kind);
        return designerEngine.loadSavedDesign(r.design.data, kind);
      })
      .then(fit)
      .catch(() => {});
  }, [designId]);

  useEffect(() => {
    if (!initialized.current) return;
    if (!logo) return;

    designerEngine.addLogo(logo);
  }, [logo]);

  return (
    <div className="w-full max-w-full rounded-3xl border border-slate-700 bg-[#1b2435] p-3 sm:p-6 shadow-2xl overflow-hidden">

      {/* Workspace */}

      <div
        ref={workspaceRef}
        className="relative flex items-center justify-center rounded-2xl bg-slate-300 p-3 sm:p-6 overflow-hidden"
      >

        {product === "tshirt" && (
          <div
            className={`absolute top-3 left-3 z-10 rounded-full px-3 py-1 text-xs sm:px-4 sm:py-1.5 sm:text-sm font-black uppercase tracking-wider shadow ${
              side === "back" ? "bg-slate-800 text-white" : "bg-cyan-600 text-white"
            }`}
          >
            {side} of shirt
          </div>
        )}

        {/* Artboard */}

        <div className="rounded-2xl bg-white shadow-xl overflow-hidden">

          <canvas ref={canvasRef} />

        </div>

      </div>

    </div>
  );
}